import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, LogOut, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { notifyCatalogChanged } from "@/context/CatalogContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityHistory } from "@/pages/admin/AdminCompanyDetail";
import { userClientIds, userStatus } from "@/pages/admin/AdminUsers";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  ReasonDialog,
  StatePill,
  UnsavedBar,
  apiErrorMessage,
} from "@/components/admin/AdminUI";

const CATEGORY_TITLES = { operacional: "Operação", estrutural: "Estrutura", configuracao: "Configurações" };
const STATES = [
  { value: "inherit", label: "Herdar" },
  { value: "granted", label: "Conceder" },
  { value: "denied", label: "Negar" },
];

function TriState({ value, onChange, disabledGrant, testId }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border" role="radiogroup">
      {STATES.map((state) => {
        const disabled = state.value === "granted" && disabledGrant && value !== "granted";
        return (
          <button
            key={state.value}
            type="button"
            role="radio"
            aria-checked={value === state.value}
            disabled={disabled}
            onClick={() => onChange(state.value)}
            className={`px-2.5 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              value === state.value
                ? state.value === "denied" ? "bg-red-500/15 text-red-600" : state.value === "granted" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-muted font-medium"
                : "hover:bg-accent"
            }`}
            data-testid={`${testId}-${state.value}`}
          >
            {state.label}
          </button>
        );
      })}
    </div>
  );
}

function revisionConflict(err) {
  return err?.response?.status === 409 && err?.response?.data?.detail?.code === "AUTHZ_REVISION_CONFLICT";
}

function ModulesTab({ userId }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin-center/users/${userId}/catalog-access`).then(({ data: payload }) => {
      setData(payload);
      setDraft({});
    }).catch((err) => toast.error(apiErrorMessage(err)));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: payload } = await api.put(`/admin-center/users/${userId}/catalog-access`, {
        changes: draft,
        expected_revision: data.authz_revision,
      });
      setData(payload);
      setDraft({});
      notifyCatalogChanged();
      toast.success("Módulos do usuário salvos e aplicados.");
    } catch (err) {
      if (revisionConflict(err)) {
        toast.error("Outra sessão alterou este acesso. A configuração salva foi recarregada.");
        load();
      } else {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    return Object.entries(CATEGORY_TITLES).map(([category, title]) => ({
      title,
      items: data.entries.filter((e) => e.category === category),
    }));
  }, [data]);

  if (!data) return <LoadingState />;
  return (
    <div className="space-y-4" data-testid="user-modules-tab">
      <p className="text-sm text-muted-foreground">
        <strong>Herdar</strong> segue o perfil dentro do contrato da empresa; <strong>Conceder</strong> e <strong>Negar</strong> ajustam este usuário.
        A coluna da direita mostra o resultado calculado pelo servidor.
      </p>
      {groups.map((group) => (
        <div key={group.title} className="rounded-xl border border-border bg-card">
          <h3 className="border-b border-border px-4 py-2.5 text-sm font-semibold">{group.title}</h3>
          <ul className="divide-y divide-border/60">
            {group.items.map((item) => {
              const value = item.key in draft ? draft[item.key] : item.state;
              return (
                <li key={item.key} className="flex flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {item.kind === null ? item.locked_reason : item.locked_reason || (item.contracted === false ? "Não contratado" : "")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {item.kind !== null && (
                      <TriState
                        value={value === "custom" ? "inherit" : value}
                        onChange={(next) => setDraft((current) => {
                          const copy = { ...current };
                          if (next === item.state) delete copy[item.key];
                          else copy[item.key] = next;
                          return copy;
                        })}
                        disabledGrant={!item.grantable}
                        testId={`user-module-${item.key}`}
                      />
                    )}
                    <StatePill tone={item.available ? "success" : "muted"} testId={`user-module-effective-${item.key}`}>
                      {item.available ? "Disponível" : "Indisponível"}
                    </StatePill>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <UnsavedBar count={Object.keys(draft).length} saving={saving} onSave={save} onDiscard={() => setDraft({})} testId="user-modules-unsaved" />
    </div>
  );
}

function ActionsTab({ userId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin-center/users/${userId}/actions`).then(({ data: payload }) => {
      setData(payload);
      setDraft({});
    }).catch((err) => toast.error(apiErrorMessage(err)));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const save = async (module) => {
    setSaving(true);
    try {
      const { data: payload } = await api.put(`/admin-center/users/${userId}/actions`, {
        module,
        actions: draft,
        expected_revision: data.authz_revision,
      });
      setData(payload);
      setDraft({});
      notifyCatalogChanged();
      toast.success("Permissões salvas.");
    } catch (err) {
      if (revisionConflict(err)) {
        toast.error("Outra sessão alterou este acesso. A configuração salva foi recarregada.");
        load();
      } else {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <LoadingState />;
  if (!data.modules.length) return <EmptyState title="Nenhum módulo disponível para este usuário" description="Conceda módulos na aba Módulos." />;
  return (
    <div className="space-y-2" data-testid="user-actions-tab">
      <p className="text-sm text-muted-foreground">Ajuste fino de operações por módulo (ex.: excluir ocorrências). Ações sensíveis estão destacadas.</p>
      {data.modules.map((module) => (
        <div key={module.module} className="rounded-xl border border-border bg-card">
          <button
            type="button"
            className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-semibold"
            onClick={() => { setOpen(open === module.module ? null : module.module); setDraft({}); }}
            aria-expanded={open === module.module}
            data-testid={`user-actions-module-${module.module}`}
          >
            {module.label}
            <span className="text-xs font-normal text-muted-foreground">
              {module.actions.filter((a) => a.effective).length}/{module.actions.length} ações
            </span>
          </button>
          {open === module.module && (
            <div className="border-t border-border">
              <ul className="divide-y divide-border/60">
                {module.actions.map((action) => (
                  <li key={action.key} className="flex flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm">
                      {action.label} {action.sensitive && <StatePill tone="warning">sensível</StatePill>}
                    </span>
                    <div className="flex items-center gap-3">
                      <TriState
                        value={action.key in draft ? draft[action.key] : action.state}
                        onChange={(next) => setDraft((current) => {
                          const copy = { ...current };
                          if (next === action.state) delete copy[action.key];
                          else copy[action.key] = next;
                          return copy;
                        })}
                        disabledGrant={!action.grantable}
                        testId={`user-action-${module.module}-${action.key}`}
                      />
                      <StatePill tone={action.effective ? "success" : "muted"}>{action.effective ? "Permitida" : "Negada"}</StatePill>
                    </div>
                  </li>
                ))}
              </ul>
              <UnsavedBar count={Object.keys(draft).length} saving={saving} onSave={() => save(module.module)} onDiscard={() => setDraft({})} testId="user-actions-unsaved" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProfileTab({ user, onSaved }) {
  const [form, setForm] = useState(null);
  const [clients, setClients] = useState([]);
  const [units, setUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    const clientIds = userClientIds(user);
    setForm({
      name: user.name || "", email: user.email || "", role: user.role || "",
      client_id: clientIds[0] || "", client_ids: clientIds,
      unit_ids: [...new Set([...(user.unit_ids || []), ...((user.scope || {}).unit_ids || [])])],
    });
  }, [user]);
  useEffect(() => { reset(); }, [reset]);
  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(Array.isArray(data) ? data : data.items || [])).catch(() => {});
    api.get("/permissions/me/delegation").then(({ data }) => setRoles(data.allowed_target_roles || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!form?.client_id) return setUnits([]);
    api.get("/units", { params: { client_id: form.client_id } })
      .then(({ data }) => setUnits(Array.isArray(data) ? data : data.items || [])).catch(() => setUnits([]));
  }, [form?.client_id]);

  if (!form) return <LoadingState />;
  const multiClient = userClientIds(user).length > 1;
  const original = {
    name: user.name || "", email: user.email || "", role: user.role || "", client_id: userClientIds(user)[0] || "",
  };
  const sameSet = (a, b) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
  const dirty = ["name", "email", "role", "client_id"].some((k) => form[k] !== original[k])
    || (multiClient && !sameSet(form.client_ids, userClientIds(user)))
    || JSON.stringify([...form.unit_ids].sort()) !== JSON.stringify([...new Set([...(user.unit_ids || []), ...((user.scope || {}).unit_ids || [])])].sort());

  const save = async () => {
    setSaving(true);
    try {
      const clientIds = multiClient ? form.client_ids : (form.client_id ? [form.client_id] : []);
      await api.put(`/users/${user.id}`, {
        name: form.name, email: form.email, role: form.role,
        company_id: user.company_id || null,
        client_id: clientIds[0] || null, client_ids: clientIds,
        unit_id: form.unit_ids[0] || null, unit_ids: form.unit_ids,
        uses_app: Boolean(user.uses_app), app_type: user.app_type || null,
      });
      toast.success("Dados e vínculos salvos.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = [...new Set([...roles, user.role])];
  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-4" data-testid="user-profile-tab">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="user-name">Nome</Label>
          <Input id="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-field-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="user-email">E-mail</Label>
          <Input id="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Perfil</Label>
          <Select value={form.role} onValueChange={(role) => setForm({ ...form, role })}>
            <SelectTrigger data-testid="user-field-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roleOptions.map((role) => <SelectItem key={role} value={role} disabled={!roles.includes(role) && role !== user.role}>{ROLE_LABELS[role] || role}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Empresa</Label>
          {multiClient ? (
            <div className="space-y-1 rounded-md border border-border p-2" data-testid="user-client-links">
              <p className="text-xs text-muted-foreground">Prestador vinculado a várias empresas; o acesso vale em todas as unidades de cada uma.</p>
              {clients.filter((c) => form.client_ids.includes(c.id) || clients.length <= 50).map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.client_ids.includes(c.id)}
                    disabled={form.client_ids.length === 1 && form.client_ids.includes(c.id)}
                    onCheckedChange={(checked) => setForm({
                      ...form,
                      client_ids: checked ? [...form.client_ids, c.id] : form.client_ids.filter((id) => id !== c.id),
                    })}
                  />
                  {c.name}
                </label>
              ))}
            </div>
          ) : (
            <Select value={form.client_id} onValueChange={(client_id) => setForm({ ...form, client_id, unit_ids: [] })}>
              <SelectTrigger><SelectValue placeholder="Sem empresa" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>
      {!multiClient && form.client_id && (
        <div className="space-y-1.5">
          <Label>Unidades (nenhuma = todas)</Label>
          <div className="grid max-h-48 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-3">
            {units.map((unit) => (
              <label key={unit.id} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm">
                <Checkbox checked={form.unit_ids.includes(unit.id)} onCheckedChange={(checked) =>
                  setForm({ ...form, unit_ids: checked ? [...form.unit_ids, unit.id] : form.unit_ids.filter((id) => id !== unit.id) })} />
                {unit.name}
              </label>
            ))}
          </div>
        </div>
      )}
      <UnsavedBar count={dirty ? 1 : 0} saving={saving} onSave={save} onDiscard={reset} testId="user-profile-unsaved" />
    </div>
  );
}

function SecurityTab({ user, onChanged }) {
  const { user: me } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [password, setPassword] = useState("");
  const status = userStatus(user);
  const self = me?.id === user.id;

  const act = async (action, reason) => {
    try {
      await api.patch(`/users/${user.id}/status`, { action, reason });
      toast.success("Status atualizado.");
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err));
      throw err;
    }
  };
  const resetPassword = async () => {
    try {
      await api.post(`/users/${user.id}/reset-password`, { password });
      toast.success("Senha redefinida. As sessões abertas foram encerradas.");
      setDialog(null);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };
  const [sessions, setSessions] = useState(null);
  const loadSessions = useCallback(() => {
    api.get(`/users/${user.id}/sessions`)
      .then(({ data }) => setSessions({ last: data.last_access, items: [...(data.sessions || [])].reverse() }))
      .catch(() => setSessions({ last: null, items: [] }));
  }, [user.id]);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  const endSessions = async () => {
    try {
      await api.post(`/users/${user.id}/end-sessions`);
      toast.success("Sessões encerradas.");
      loadSessions();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="user-security-tab">
      <p className="text-sm">Status atual: <StatePill tone={status.tone} testId="user-status">{status.label}</StatePill></p>
      <div className="flex flex-wrap gap-2">
        {user.active === false ? (
          <Button size="sm" onClick={() => act("ativar")} disabled={self} data-testid="user-activate">Reativar usuário</Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setDialog("deactivate")} disabled={self} data-testid="user-deactivate">Desativar usuário</Button>
        )}
        <Button size="sm" variant="outline" onClick={() => { setPassword(""); setDialog("password"); }} data-testid="user-reset-password">
          <KeyRound size={14} className="mr-1" /> Redefinir senha
        </Button>
        <Button size="sm" variant="outline" onClick={endSessions} disabled={self} data-testid="user-end-sessions">
          <LogOut size={14} className="mr-1" /> Encerrar sessões
        </Button>
      </div>
      {self && <p className="text-xs text-muted-foreground">Você não pode desativar nem encerrar a própria sessão por aqui.</p>}
      <div className="border-t border-border pt-3" data-testid="user-sessions">
        <p className="text-xs font-semibold text-muted-foreground">
          Acessos recentes{sessions?.last ? ` · último em ${formatDate(sessions.last)}` : ""}
        </p>
        {sessions === null ? <LoadingState /> : sessions.items.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">Nenhum acesso registrado.</p>
        ) : (
          <ul className="mt-1 divide-y divide-border/60 text-xs">
            {sessions.items.map((item, index) => (
              <li key={`${item.login_at}-${index}`} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <span className="min-w-0 truncate" title={item.device}>{item.device || "Dispositivo não informado"}</span>
                <span className="text-muted-foreground">{item.ip || "—"} · {formatDate(item.login_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ReasonDialog
        open={dialog === "deactivate"}
        onOpenChange={(open) => !open && setDialog(null)}
        title="Desativar usuário"
        description="O usuário perde o acesso imediatamente. Histórico e registros são preservados."
        confirmLabel="Desativar"
        destructive
        onConfirm={(reason) => act("desativar", reason)}
        testId="user-deactivate-dialog"
      />
      <Dialog open={dialog === "password"} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Redefinir senha</DialogTitle></DialogHeader>
          <Label htmlFor="new-password">Nova senha (mínimo 8 caracteres)</Label>
          <Input id="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="user-new-password" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={resetPassword} disabled={password.length < 8} data-testid="user-new-password-confirm">Redefinir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminUserDetail() {
  const { userId } = useParams();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.items || [];
      const found = list.find((u) => u.id === userId);
      if (found) setUser(found);
      else setError("Usuário não encontrado no seu escopo.");
    }).catch((err) => setError(apiErrorMessage(err)));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <EmptyState title={error} />;
  if (!user) return <LoadingState />;
  const tab = params.get("tab") || "modulos";
  const status = userStatus(user);
  const inPermissionsCenter = location.pathname.startsWith("/admin/permissoes");
  const usersBackTo = inPermissionsCenter ? "/admin/permissoes/usuarios" : "/admin/usuarios";
  return (
    <div className="space-y-5" data-testid="admin-user-detail">
      <Link to={usersBackTo} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft size={13} /> Usuários
      </Link>
      <PageHeader
        icon={UserCog}
        title={user.name}
        description={`${user.email} · ${ROLE_LABELS[user.role] || user.role}`}
        actions={
          <div className="flex items-center gap-2">
            <StatePill tone={status.tone}>{status.label}</StatePill>
            {!inPermissionsCenter && (
              // Gestão de Usuários cuida da CONTA; a concessão/revogação
              // acontece na Central Única de Permissões.
              <Button size="sm" variant="outline" asChild>
                <Link
                  to={`/admin/permissoes/usuarios/${userId}`}
                  data-testid="user-open-permissions-center"
                >
                  Gerenciar permissões
                </Link>
              </Button>
            )}
          </div>
        }
      />
      <Tabs value={tab} onValueChange={(value) => setParams({ tab: value })}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="modulos" data-testid="user-tab-modulos">Módulos</TabsTrigger>
          <TabsTrigger value="acoes" data-testid="user-tab-acoes">Permissões detalhadas</TabsTrigger>
          <TabsTrigger value="perfil" data-testid="user-tab-perfil">Perfil e vínculos</TabsTrigger>
          <TabsTrigger value="seguranca" data-testid="user-tab-seguranca">Segurança</TabsTrigger>
          <TabsTrigger value="historico" data-testid="user-tab-historico">Histórico</TabsTrigger>
        </TabsList>
        <TabsContent value="modulos"><ModulesTab userId={userId} /></TabsContent>
        <TabsContent value="acoes"><ActionsTab userId={userId} /></TabsContent>
        <TabsContent value="perfil"><ProfileTab user={user} onSaved={load} /></TabsContent>
        <TabsContent value="seguranca"><SecurityTab user={user} onChanged={load} /></TabsContent>
        <TabsContent value="historico"><EntityHistory entityId={userId} /></TabsContent>
      </Tabs>
    </div>
  );
}
